import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LEAD_SOURCES, DealStage } from "@/types/dealTracker";
import { Loader2, Link } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface NewLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: NewLeadFormData) => Promise<void>;
  isLoading?: boolean;
}

export interface NewLeadFormData {
  seller_names: string;
  phone_number?: string;
  email?: string;
  mailing_address?: string;
  property_addresses?: string;
  expected_commission?: string;
  lead_source: string;
  deal_stage: DealStage;
  notes?: string;
  property_id?: string;
}

const NewLeadModal = ({ open, onOpenChange, onSubmit, isLoading }: NewLeadModalProps) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<NewLeadFormData>({
    seller_names: '',
    phone_number: '',
    email: '',
    mailing_address: '',
    property_addresses: '',
    expected_commission: '',
    lead_source: '',
    deal_stage: 'purchase_agreement_signed',
    notes: '',
    property_id: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: properties = [] } = useQuery({
    queryKey: ['seller-properties-list', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('properties')
        .select('id, address, city, state')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && open,
  });
  
  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.seller_names.trim()) {
      newErrors.seller_names = 'Seller name is required';
    }
    if (!formData.lead_source) {
      newErrors.lead_source = 'Lead source is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    const submitData = { ...formData };
    if (!submitData.property_id) {
      delete submitData.property_id;
    }
    
    await onSubmit(submitData);
    
    // Reset form
    setFormData({
      seller_names: '',
      phone_number: '',
      email: '',
      mailing_address: '',
      property_addresses: '',
      expected_commission: '',
      lead_source: '',
      deal_stage: 'purchase_agreement_signed',
      notes: '',
      property_id: '',
    });
    setErrors({});
  };
  
  const handleChange = (field: keyof NewLeadFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePropertyLink = (propertyId: string) => {
    if (propertyId === 'none') {
      handleChange('property_id', '');
      return;
    }
    handleChange('property_id', propertyId);
    // Auto-fill property address if empty
    if (!formData.property_addresses) {
      const prop = properties.find(p => p.id === propertyId);
      if (prop) {
        handleChange('property_addresses', `${prop.address}, ${prop.city}, ${prop.state}`);
      }
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Add New Deal</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="seller_names">
              Seller Name(s) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="seller_names"
              value={formData.seller_names}
              onChange={(e) => handleChange('seller_names', e.target.value)}
              placeholder="John & Jane Doe"
              className={errors.seller_names ? 'border-destructive' : ''}
            />
            {errors.seller_names && (
              <p className="text-sm text-destructive">{errors.seller_names}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone</Label>
              <Input
                id="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="mailing_address">Mailing Address</Label>
            <Input
              id="mailing_address"
              value={formData.mailing_address}
              onChange={(e) => handleChange('mailing_address', e.target.value)}
              placeholder="123 Main St, City, State 12345"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="property_addresses">Property Address(es)</Label>
            <Textarea
              id="property_addresses"
              value={formData.property_addresses}
              onChange={(e) => handleChange('property_addresses', e.target.value)}
              placeholder="456 Oak Ave, City, State 12345&#10;789 Pine Rd, City, State 12345"
              rows={2}
            />
          </div>

          {/* Link to Deal dropdown */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Link className="h-3.5 w-3.5" />
              Link to Uploaded Deal
            </Label>
            <Select
              value={formData.property_id || 'none'}
              onValueChange={handlePropertyLink}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a deal to link" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.address}, {prop.city}, {prop.state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expected_commission">Expected Commission</Label>
              <Input
                id="expected_commission"
                value={formData.expected_commission}
                onChange={(e) => handleChange('expected_commission', e.target.value)}
                placeholder="$12,500 or 2.5%"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lead_source">
                Deal Source <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.lead_source}
                onValueChange={(value) => handleChange('lead_source', value)}
              >
                <SelectTrigger className={errors.lead_source ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.lead_source && (
                <p className="text-sm text-destructive">{errors.lead_source}</p>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes about this lead..."
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Lead'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewLeadModal;
